import React, { useMemo } from 'react';
import { Box, Text, Flex, Divider } from '@chakra-ui/react';
import { getUserDisplayName, formatRefundDateTime } from 'api/administrador/refunds';

const PURPLE = '#574feb';

function getEventActionText(event) {
  switch (event.action) {
    case 'registered':
      return 'registró la devolución';
    case 'marked_retained':
      return 'marcó: "Estudiante retenido — caso cerrado"';
    case 'proceeded_refund':
      return 'marcó: "No se logró retener — Procede devolución"';
    case 'completed':
      return 'confirmó la devolución completada';
    default:
      return event.detail || '';
  }
}

function buildTimelineEvents(events, refund) {
  const sorted = [...(events || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const hasRegistered = sorted.some((event) => event.action === 'registered');
  if (!hasRegistered && refund?.registered_by) {
    sorted.unshift({
      id: `registered-${refund.id}`,
      action: 'registered',
      user: refund.registered_by,
      createdAt: refund.createdAt,
      note: null,
      detail: `${getUserDisplayName(refund.registered_by)} registró la devolución`,
    });
  }

  return sorted;
}

function TimelineDot({ isPending = false }) {
  if (isPending) {
    return (
      <Box
        w="10px"
        h="10px"
        borderRadius="full"
        border="2px solid"
        borderColor="gray.300"
        bg="white"
        flexShrink={0}
        mt={1}
        position="relative"
        zIndex={1}
      />
    );
  }

  return (
    <Box
      w="10px"
      h="10px"
      borderRadius="full"
      bg={PURPLE}
      flexShrink={0}
      mt={1}
      position="relative"
      zIndex={1}
    />
  );
}

function EventActionLine({ event }) {
  const userName = getUserDisplayName(event.user);
  const actionText = getEventActionText(event);

  if (event.action && userName && userName !== 'Usuario desconocido') {
    return (
      <Text fontSize="sm" color="gray.800" lineHeight="tall">
        <Text as="span" fontWeight="bold" color={PURPLE}>
          {userName}
        </Text>{' '}
        {actionText}
      </Text>
    );
  }

  return (
    <Text fontSize="sm" color="gray.800" lineHeight="tall">
      {event.detail}
    </Text>
  );
}

function TimelineItem({ event, showDivider }) {
  const dateStr = formatRefundDateTime(event.createdAt);

  return (
    <Box>
      <Flex gap={3} align="flex-start">
        <TimelineDot />
        <Box flex={1} minW={0} pb={showDivider ? 4 : 0}>
          <Text fontSize="xs" color="gray.500" mb={1.5}>
            {dateStr}
          </Text>
          <EventActionLine event={event} />
          {event.note && (
            <Box mt={3} p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" color="gray.700" fontStyle="italic" lineHeight="tall">
                {event.note}
              </Text>
            </Box>
          )}
        </Box>
      </Flex>
      {showDivider && <Divider borderColor="gray.200" ml={6} mb={4} />}
    </Box>
  );
}

function PendingItem({ title, subtitle, showDivider = false }) {
  return (
    <Box>
      <Flex gap={3} align="flex-start">
        <TimelineDot isPending />
        <Box flex={1} pb={showDivider ? 4 : 0}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={0.5}>
            {title}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {subtitle}
          </Text>
        </Box>
      </Flex>
      {showDivider && <Divider borderColor="gray.200" ml={6} />}
    </Box>
  );
}

function getPendingInfo(status) {
  if (status === 'ready_for_payment') {
    return {
      title: 'Esperando acción',
      subtitle: 'Pendiente comprobante de transferencia',
    };
  }
  if (status === 'pending') {
    return {
      title: 'Esperando acción',
      subtitle: 'Pendiente revisión de retención',
    };
  }
  return null;
}

export default function CaseHistoryTimeline({ events, status, refund }) {
  const timelineEvents = useMemo(
    () => buildTimelineEvents(events, refund),
    [events, refund]
  );

  const pendingInfo = getPendingInfo(status);

  return (
    <Box bg="white" borderRadius="lg" border="1px solid #9e46d8" overflow="hidden" mt={4}>
      <Box bg={PURPLE} px={6} py={3}>
        <Text color="white" fontWeight="bold" fontSize="md">
          Historial
        </Text>
      </Box>

      <Box px={6} py={5} position="relative">
        {timelineEvents.length > 0 && (
          <Box
            position="absolute"
            left="28px"
            top="18px"
            bottom={pendingInfo ? '48px' : '18px'}
            w="2px"
            bg="gray.200"
            zIndex={0}
          />
        )}

        {timelineEvents.map((event, index) => (
          <TimelineItem
            key={event.id}
            event={event}
            showDivider={index < timelineEvents.length - 1 || !!pendingInfo}
          />
        ))}

        {pendingInfo && (
          <PendingItem title={pendingInfo.title} subtitle={pendingInfo.subtitle} />
        )}

        {!timelineEvents.length && !pendingInfo && (
          <Text fontSize="sm" color="gray.500">
            Sin eventos registrados.
          </Text>
        )}
      </Box>
    </Box>
  );
}
